import { createReducer } from '@reduxjs/toolkit';
import { GridItem, GridItemEntities, QueryResultsState } from './models';
import {
  loadHTMLPreviewFailure,
  loadHTMLPreviewSuccess,
  loadQueryResultsStart,
  loadTabularDataFailure,
  loadTabularDataSuccess,
  loadDownloadStart,
  loadDownloadFailure,
  loadDownloadSuccess,
  loadDownloadProgress,
  loadDownloadCancel,
  loadQDSearchFailure,
  loadQDSearchStart,
  loadQDSearchSuccess,
  resetForDownload,
  selectForDownload
} from './queryResults.actions';

const INITIAL_STATE: QueryResultsState = {
  itemRequest: null,
  previewHTML: '',
  gridItemEntities: null,
  gridItemCount: -1,
  idsForDownload: null,
  downloading: false,
  downloadProgress: null,
  tabLoading: false,
  tabLoaded: false,
  prevLoading: false,
  prevLoaded: false,
  searchLoading: false,
  searchLoaded: false,
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  errMsg: null
};

export const queryResultsReducer = createReducer(INITIAL_STATE, (builder) => {
  builder
    .addCase( loadQueryResultsStart, (state) => ({
      ...state,
      itemRequest: null,
      prevLoading: true,
      prevLoaded: false,
      previewHTML: '',
      tabLoading: true,
      tabLoaded: false,
      searchLoaded: false,
      searchLoading: false,
      gridItemEntities: null,
      gridItemCount: -1,
      idsForDownload: null
    }))
    .addCase( loadTabularDataSuccess, (state, action) => ({
      ...state,
      itemRequest: action.payload.itemCount? action.payload.itemRequest : null,
      tabLoading: false,
      tabLoaded: !!action.payload.itemCount,
      gridItemCount: action.payload.itemCount,
      gridItemEntities: action.payload.itemCount ?
        action.payload.gridItemsList.reduce(
          (entities: GridItemEntities, gridItem: GridItem): GridItemEntities => {
            return {
              ...entities,
              [gridItem.itemTableIDAsInt]: {...gridItem, isSelected: false}
            };
          },
          {}
        ) :
        null
    }))
    .addCase( loadTabularDataFailure, (state, action) => ({
      ...state,
      itemRequest: null,
      tabLoading: false,
      tabLoaded: false,
      gridItemCount: -1,
      gridItemEntities: null,
      errMsg: action.payload
    }))
    .addCase( loadHTMLPreviewSuccess, (state, action) => ({
      ...state,
      prevLoading: false,
      prevLoaded: !!action.payload.itemCount,
      previewHTML: action.payload.itemCount ? action.payload.previewHTML : ''
    }))
    .addCase( loadHTMLPreviewFailure, (state, action) => ({
      ...state,
      prevLoading: false,
      prevLoaded: false,
      errMsg: action.payload
    }))
    .addCase( loadQDSearchStart, (state) => ({
      ...state,
      searchLoading: true,
      searchLoaded: false,
    }))
    .addCase( loadQDSearchSuccess, (state, action) => ({
      ...state,
      itemRequest: action.payload.itemRequest,
      searchLoading: false,
      searchLoaded: true,
      gridItemCount: action.payload.itemCount,
      gridItemEntities: action.payload.gridItemsList.reduce(
        (entities: GridItemEntities, gridItem: GridItem): GridItemEntities => {
          return {
            ...entities,
            [gridItem.itemTableIDAsInt]: {...gridItem, isSelected: false}
          };
        },
        {}
      )
    }))
    .addCase( loadQDSearchFailure, (state, action) => ({
      ...state,
      searchLoading: false,
      searchLoaded: false,
      errMsg: action.payload
    }))
    .addCase( loadDownloadStart, (state) => ({
      ...state,
      downloading: true,
      downloadProgress: null,
      errMsg: null
    }))
    .addCase( loadDownloadSuccess, (state) => ({
      ...state,
      downloading: false,
      downloadProgress: null,
      errMsg: null
    }))
    .addCase( loadDownloadFailure, (state, action) => ({
      ...state,
      downloading: false,
      downloadProgress: null,
      errMsg: action.payload
    }))
    .addCase( loadDownloadProgress, (state, action) => ({
      ...state,
      downloadProgress: action.payload
    }))
    .addCase( loadDownloadCancel, (state) => ({
      ...state,
      downloading: false,
      downloadProgress: null,
      errMsg: null
    }))
    .addCase( selectForDownload, (state, action) => ({
      ...state,
      idsForDownload: action.payload
    }))
    .addCase( resetForDownload, (state) => ({
      ...state,
      idsForDownload: null
    }));
});

export default queryResultsReducer;